import { useCallback } from 'react';
import React from 'react';
import { clarificationService } from '@/services/ai/ClarificationService';
import { propertyModificationService } from '@/services/ai/PropertyModificationService';
import { styleCloneService, type StyleCloneScope } from '@/services/ai/StyleCloneService';
import { useEditorStore } from '@/stores/editor/editor-store';
import type {
  ClarificationRequest,
  ClarificationResponse,
  ClarificationHistoryEntry,
} from '@/types/ai';
import type { PropertyMessage } from '@/components/ai/types';
import type { PropertyChange } from '@/services/ai/commands/BaseCommandHandler';
import type { ComponentPropertyChanges } from './agentMessagingTypes';

function getStyleCloneScope(value: unknown): StyleCloneScope {
  switch (value) {
    case 'all':
    case 'colors':
    case 'typography':
    case 'spacing':
    case 'borders':
    case 'shadows':
    case 'layout':
      return value;
    default:
      return 'all';
  }
}

function toComponentPropertyChanges(
  changes: PropertyChange,
  targetComponentId?: string
): ComponentPropertyChanges {
  return targetComponentId
    ? { ...changes, targetComponentId }
    : { ...changes };
}

// Module-level helper: extract the specific modification action from a command
function extractModificationAction(command: string): string {
  const trimmedCommand = command.trim();

  // PRIORITY 1: Check for known action keywords first (most reliable)
  const actionKeywords = /\b(bigger|larger|smaller|tinier|bold|italic|underline|uppercase|lowercase|red|blue|green|yellow|orange|purple|pink|white|black|gray|grey|primary|secondary|hidden|visible|transparent|opaque|center|left|right|justify)\b/i;
  const keywordMatch = trimmedCommand.match(actionKeywords);
  if (keywordMatch) {
    console.log('[extractModificationAction] Found action keyword:', keywordMatch[1]);
    return keywordMatch[1].trim();
  }

  // PRIORITY 2: Extract explicit values like "24px", "100%", "#ff0000"
  const valuePatterns = [
    /\b(\d+(?:\.\d+)?(?:px|rem|em|%|vh|vw))\b/i,
    /(#[0-9a-f]{3,8})\b/i,
    /(rgba?\([^)]+\))/i,
  ];

  for (const pattern of valuePatterns) {
    const match = trimmedCommand.match(pattern);
    if (match && match[1]) {
      console.log('[extractModificationAction] Found value:', match[1]);
      return match[1].trim();
    }
  }

  // PRIORITY 3: Extract from "change/set X to Y" patterns - get Y
  const toPatterns = [
    /(?:change|set)\s+\S+\s+to\s+(.+)$/i,
    /(?:make|set)\s+\S+\s+(.+)$/i,
  ];

  for (const pattern of toPatterns) {
    const match = trimmedCommand.match(pattern);
    if (match && match[1]) {
      const innerKeyword = match[1].match(actionKeywords);
      if (innerKeyword) {
        console.log('[extractModificationAction] Extracted keyword from phrase:', innerKeyword[1]);
        return innerKeyword[1].trim();
      }
      console.log('[extractModificationAction] Extracted action phrase:', match[1]);
      return match[1].trim();
    }
  }

  // PRIORITY 4: Fallback - remove common component reference words and return the rest
  const cleanedCommand = trimmedCommand
    .replace(/^(make|change|set|update)\s+/i, '')
    .replace(/\b(the|a|an|this|that|it|its)\s+/gi, '')
    .replace(/\b(header|title|button|text|card|container|section|sidebar|footer|nav|navigation|menu|image|chart|table|input|form|component)\b/gi, '')
    .trim();

  console.log('[extractModificationAction] Fallback cleaned command:', cleanedCommand);
  return cleanedCommand || trimmedCommand;
}

export interface UseClarificationFlowDeps {
  pendingClarification: ClarificationRequest | null;
  clearPendingClarification: () => void;
  addClarificationToHistory: (entry: ClarificationHistoryEntry) => void;
  setMessages: React.Dispatch<React.SetStateAction<PropertyMessage[]>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  generateMessageId: () => string;
  applyPropertyChanges: (id: string, changes: ComponentPropertyChanges) => void;
  persistMessage: (msg: PropertyMessage) => Promise<void>;
}

export interface UseClarificationFlowReturn {
  handleClarificationResponse: (response: ClarificationResponse) => Promise<void>;
  handleClarificationCancel: () => void;
}

export function useClarificationFlow(deps: UseClarificationFlowDeps): UseClarificationFlowReturn {
  const {
    pendingClarification,
    clearPendingClarification,
    addClarificationToHistory,
    setMessages,
    setIsLoading,
    setInputValue,
    generateMessageId,
    applyPropertyChanges,
    persistMessage,
  } = deps;

  const handleClarificationResponse = useCallback(async (response: ClarificationResponse) => {
    if (!pendingClarification) {
      return;
    }

    // Process the response
    const result = clarificationService.processClarificationResponse(response);

    if (!result) {
      console.error('[AIPropertiesChat] Failed to process clarification response');
      clearPendingClarification();
      return;
    }

    // Add history entry
    const historyEntry: ClarificationHistoryEntry = {
      request: pendingClarification,
      response,
      wasSuccessful: true,
    };
    addClarificationToHistory(historyEntry);

    // Clear the pending clarification
    clearPendingClarification();

    // Build the resolved command with clarified values
    const originalCommand = pendingClarification.context.originalCommand;

    // Phase 2.3: Handle style clone clarification
    if (pendingClarification.context.detectedIntent === 'style_clone' && result.resolvedComponentId) {
      console.log('[Phase 2.3] Processing clone clarification response');

      const { components } = useEditorStore.getState();
      const sourceComponent = components.find(c => c.id === result.resolvedComponentId);
      const targetComponentId = pendingClarification.context.targetComponentId;
      const targetComponent = targetComponentId ? components.find(c => c.id === targetComponentId) : null;
      const cloneScope = getStyleCloneScope(pendingClarification.context.currentValues?.cloneScope);

      if (sourceComponent && targetComponent) {
        // Add confirmation message
        const confirmMessage: PropertyMessage = {
          id: generateMessageId(),
          content: `Copying ${cloneScope === 'all' ? 'styles' : cloneScope} from "${sourceComponent.name || sourceComponent.type}"...`,
          role: 'assistant',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, confirmMessage]);
        setIsLoading(true);

        try {
          const extractedStyles = styleCloneService.extractStylesForClone(sourceComponent, cloneScope);
          const filteredResult = styleCloneService.filterAndBuildChanges(extractedStyles, targetComponent, cloneScope);

          if (filteredResult.propertyChanges && Object.keys(filteredResult.propertyChanges).length > 0) {
            const changes = {
              ...filteredResult.propertyChanges,
              targetComponentId: targetComponent.id
            };
            applyPropertyChanges(generateMessageId(), changes);

            const description = `Copied ${cloneScope === 'all' ? 'styles' : cloneScope} from "${sourceComponent.name || sourceComponent.type}" to "${targetComponent.name || targetComponent.type}"`;
            const successMessage: PropertyMessage = {
              id: generateMessageId(),
              content: `✅ ${description}`,
              role: 'assistant',
              timestamp: new Date(),
              applied: true,
            };
            setMessages(prev => prev.filter(m => m.content !== confirmMessage.content).concat(successMessage));

            if (filteredResult.warnings.length > 0) {
              console.log('[Phase 2.3] Clone warnings:', filteredResult.warnings);
            }
          } else {
            const errorMessage: PropertyMessage = {
              id: generateMessageId(),
              content: `⚠️ No compatible ${cloneScope === 'all' ? 'styles' : cloneScope} found to copy from "${sourceComponent.name || sourceComponent.type}"`,
              role: 'assistant',
              timestamp: new Date(),
            };
            setMessages(prev => prev.filter(m => m.content !== confirmMessage.content).concat(errorMessage));
          }
        } catch (error) {
          console.error('[Phase 2.3] Clone failed:', error);
          const errorMessage: PropertyMessage = {
            id: generateMessageId(),
            content: `❌ Failed to copy styles: ${error instanceof Error ? error.message : 'Unknown error'}`,
            role: 'assistant',
            timestamp: new Date(),
          };
          setMessages(prev => prev.filter(m => m.content !== confirmMessage.content).concat(errorMessage));
        } finally {
          setIsLoading(false);
        }
      } else {
        console.error('[Phase 2.3] Clone clarification failed - missing components');
        setIsLoading(false);
      }
      return;
    }

    // If a component was selected, set it as the target
    if (result.resolvedComponentId) {
      const { components } = useEditorStore.getState();
      const resolvedComponent = components.find(c => c.id === result.resolvedComponentId);

      if (resolvedComponent) {
        const confirmMessage: PropertyMessage = {
          id: generateMessageId(),
          content: `Applying changes to "${resolvedComponent.name || resolvedComponent.type}"...`,
          role: 'assistant',
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, confirmMessage]);

        setIsLoading(true);

        setTimeout(async () => {
          try {
            const actionCommand = extractModificationAction(originalCommand);

            const targetComponent = resolvedComponent;
            const changes = await propertyModificationService.parsePropertyChangesAsync(
              actionCommand,
              targetComponent
            );

            const hasActualChanges = changes && (
              (changes.properties && Object.keys(changes.properties).length > 0) ||
              (changes.style && Object.keys(changes.style).length > 0) ||
              (changes.size && Object.keys(changes.size).length > 0) ||
              changes.position || changes.data || changes.type
            );
            if (hasActualChanges && changes) {
              const resolvedChanges = toComponentPropertyChanges(
                changes,
                result.resolvedComponentId
              );
              applyPropertyChanges(generateMessageId(), resolvedChanges);

              const successMessage: PropertyMessage = {
                id: generateMessageId(),
                content: `✓ Applied changes to "${resolvedComponent.name || resolvedComponent.type}"`,
                role: 'assistant',
                timestamp: new Date(),
                applied: true,
              };
              setMessages(prev => prev.filter(m => m.content !== confirmMessage.content).concat(successMessage));
              persistMessage(successMessage);
            } else {
              const errorMessage: PropertyMessage = {
                id: generateMessageId(),
                content: `I understood you want to modify "${resolvedComponent.name || resolvedComponent.type}" with "${actionCommand}", but I couldn't apply that change. Try commands like "bigger", "smaller", "red", "bold", or specific values like "24px".`,
                role: 'assistant',
                timestamp: new Date(),
              };
              setMessages(prev => prev.filter(m => m.content !== confirmMessage.content).concat(errorMessage));
            }
          } catch (error) {
            console.error('[AIPropertiesChat] Error processing clarified command:', error);
            const errorMessage: PropertyMessage = {
              id: generateMessageId(),
              content: `Error applying changes. Please try again.`,
              role: 'assistant',
              timestamp: new Date(),
            };
            setMessages(prev => [...prev, errorMessage]);
          } finally {
            setIsLoading(false);
            setInputValue("");
          }
        }, 100);
      }
    } else if (result.resolvedValue !== undefined) {
      const valueMessage: PropertyMessage = {
        id: generateMessageId(),
        content: `Applying change with value: ${result.resolvedValue}`,
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, valueMessage]);
      persistMessage(valueMessage);

      // TODO: Re-process the command with the resolved value
    } else if (result.resolvedBreakpoint) {
      const breakpointMessage: PropertyMessage = {
        id: generateMessageId(),
        content: `Applying changes for ${result.resolvedBreakpoint} breakpoint...`,
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, breakpointMessage]);
      persistMessage(breakpointMessage);

      // TODO: Re-process the command with the resolved breakpoint
    }
  }, [pendingClarification, clearPendingClarification, addClarificationToHistory, generateMessageId, applyPropertyChanges, persistMessage]);

  // Handle cancelling clarification
  const handleClarificationCancel = useCallback(() => {
    clearPendingClarification();

    const cancelMessage: PropertyMessage = {
      id: generateMessageId(),
      content: "Okay, I'll skip this question. Let me know if you'd like to try again.",
      role: 'assistant',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, cancelMessage]);
    persistMessage(cancelMessage);
  }, [clearPendingClarification, generateMessageId, persistMessage]);

  return { handleClarificationResponse, handleClarificationCancel };
}
